import { p as proxyCustomElement, H, c as createEvent, h, F as Fragment, a as Host } from './index.js';
import { g as getAsChild } from './p-CmanOHks.js';
import { f as forwardEvent } from './p-rUtJRH95.js';
import { p as parseHrefProp } from './p-DlCOw5Kl.js';
import { w as wrapInTooltip } from './p-C0G3ZH28.js';
import { d as defineCustomElement$3 } from './p-aAvJ0uEW.js';
import { d as defineCustomElement$2 } from './p-BqnaoLp8.js';
import { d as defineCustomElement$1 } from './p-B4Sv5V7D.js';

const tabCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;align-items:center;box-sizing:border-box;color:var(--jw-theme-surface-text-description-color);display:inline-flex;flex:none;flex-direction:column;font-family:Open Sans;font-size:.875rem;font-weight:400;font-weight:600;letter-spacing:-.0004em;line-height:normal;line-height:1.25rem;max-width:-webkit-fit-content;max-width:fit-content}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}button{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;align-items:center;background:none;border:none;box-sizing:border-box;cursor:pointer;display:inline-flex;font-family:Open Sans;font-size:.875rem;font-weight:400;font-weight:inherit;justify-content:center;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;margin:0;padding:0;-webkit-user-select:none;user-select:none}button *,button :after,button :before{box-sizing:inherit}button img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}button[disabled]{cursor:not-allowed}button:focus{outline:none}a{align-items:center;display:inline-flex;justify-content:center;line-height:normal}a,a:hover{text-decoration:none}.inner{color:inherit;transition:color .2s ease}.label{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}:host([type=tab]){border-bottom:2px solid #0000;color:var(--jw-theme-surface-text-description-color);transition:border-color .2s ease}:host([type=tab]) .inner{padding:.75rem 0 calc(.75rem - 2px)}:host([type=tab]:not([disabled]):not([active]):hover){color:var(--jw-theme-surface-text-default-color)}:host([type=tab][active]){border-color:var(--jw-theme-system-surface-color);color:var(--jw-theme-system-surface-color)}:host([type=tab-button]){background-color:var(--jw-theme-surface-item-default-bg-color);color:var(--jw-theme-surface-text-description-color);font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;transition:background-color .2s ease}:host([type=tab-button]) .inner{height:calc(2rem - 2px);padding:0 1rem}:host([type=tab-button]:not([disabled]):hover){background-color:var(--jw-theme-surface-item-hover-bg-color)}:host([type=tab-button][active]:not([disabled])){background-color:var(--jw-theme-surface-item-active-bg-color);color:var(--jw-theme-surface-item-active-color)}:host([active]),:host([active]) a,:host([active]) button{cursor:default}:host([disabled]){color:var(--jw-theme-surface-text-disabled-color)}:host([disabled]),:host([disabled]) a{cursor:not-allowed}";

const WuiTab = /*@__PURE__*/ proxyCustomElement(class WuiTab extends H {
    constructor(registerHost) {
        super();
        if (registerHost !== false) {
            this.__registerHost();
        }
        this.__attachShadow();
        this.activate = createEvent(this, "activate", 3);
        /**
         * Text label
         */
        this.label = undefined;
        /**
         * Icon to show in place of text label
         * Only valid for type tab-button
         */
        this.icon = null;
        /**
         * Display type
         */
        this.type = 'tab';
        /**
         * If tab is active/selected
         */
        this.active = false;
        /**
         * If tab is disabled
         */
        this.disabled = false;
        /**
         * Custom HTML element to render as
         */
        this.as = ['button', {}];
    }
    async _handleKeyDown(e) {
        if (e.key === 'Enter') {
            if (this.active || this.disabled) {
                return;
            }
            this.activate.emit();
            return true; // we handled this event
        }
    }
    render() {
        const aProps = this.as?.[0] === 'a' ? this.as[1] : undefined;
        const content = getAsChild(this.as || ['button', {}], h(Fragment, null, this.icon ? h("wui-icon", { name: this.icon }) : h("span", { class: "label" }, this.label)), {
            button: {
                disabled: this.disabled,
                type: 'button',
                class: 'inner',
                onClick: (e) => {
                    if (this.disabled) {
                        e.preventDefault();
                        return;
                    }
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    if (this.active) {
                        return;
                    }
                    forwardEvent(e, this.elm);
                    this.activate.emit();
                },
            },
            a: {
                ...parseHrefProp(aProps, this.disabled),
                class: 'inner',
            },
        });
        return h(Host, null, wrapInTooltip(this.tooltip, content));
    }
    get elm() { return this; }
    static get style() { return tabCss; }
}, [257, "wui-tab", {
        "label": [1],
        "icon": [1],
        "type": [513],
        "active": [516],
        "disabled": [516],
        "tooltip": [1],
        "as": [16]
    }, [[0, "keydown", "_handleKeyDown"]]]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-tab", "wui-icon", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-tab":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiTab);
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { WuiTab as W, defineCustomElement as d };
